import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Loader2 } from "lucide-react";
import workvivoNotificationsService from "../../services/workvivoNotificationsService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";

const NotificationsTab = () => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["me", "workvivo-notification-preference"],
    queryFn: () => workvivoNotificationsService.getMyPreference(),
  });

  // Seed from the server once; background refetches must not flip the
  // toggle mid-interaction.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!seededRef.current && data && typeof data.enabled === "boolean") {
      setEnabled(data.enabled);
      seededRef.current = true;
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (next) => workvivoNotificationsService.setMyPreference(next),
    onSuccess: (updated) => {
      setEnabled(updated.enabled);
      queryClient.invalidateQueries({ queryKey: ["me", "workvivo-notification-preference"] });
      toast.success("Notification preference saved");
    },
    onError: (err) => {
      toast.error(formatError(err));
      if (data) setEnabled(data.enabled);
    },
  });

  const handleToggle = () => {
    const next = !enabled;
    setEnabled(next);
    mutation.mutate(next);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-start gap-3 mb-6">
        <Bell className="w-6 h-6 text-indigo-600 mt-0.5" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
          <p className="text-sm text-gray-600 mt-1">
            Control whether the in-app notifications you receive are also
            mirrored to your Workvivo app as push or email alerts. The in-app
            bell always works regardless of this setting.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-white">
        <div>
          <p className="font-medium text-gray-900">Receive Workvivo notifications</p>
          <p className="text-sm text-gray-500 mt-0.5">
            When off, no Workvivo push/email alerts will be sent for your
            in-app notifications.
          </p>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={mutation.isPending}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
            enabled ? "bg-indigo-600" : "bg-gray-300"
          }`}
          aria-pressed={enabled}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default NotificationsTab;
